import { type FocusEventHandler, type Ref, useCallback, useEffect, useState } from 'react';

import AIStarsIcon from '@/assets/svg/ai-stars.svg?react';
import SendIcon from '@/assets/svg/bx-send.svg?react';
import { Button, MicrophoneButton } from '@/components/buttons';
import { InputGroup, InputGroupAddon, InputGroupTextInput } from '@/components/Form';
import useForwardedRef from '@/hooks/useForwardedRef';
import useSpeechRecognition from '@/hooks/useSpeechRecognition';
import { cn } from '@/lib/utils';

type Props = {
  ref?: Ref<HTMLInputElement>;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  isRound?: boolean;
  isSearchDisabled?: boolean;
  showAIIcon?: boolean;
  showSendButton?: boolean;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  onSearch: (value: string) => boolean;
  onFocus?: () => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
} & React.ComponentProps<typeof InputGroupTextInput>;

const AISearchInput = ({
  ref,
  className = '',
  size = 'large',
  isRound = false,
  isSearchDisabled = false,
  placeholder,
  showAIIcon = true,
  showSendButton = false,
  value: controlledValue,
  onValueChange,
  onSearch,
  onFocus,
  onBlur,
  ...rest
}: Props) => {
  const inputRef = useForwardedRef<HTMLInputElement>(ref);
  const [textValue, setTextValue] = useState('');
  const {
    value: audioValue,
    setValue: setAudioValue,
    isRecording,
    startRecording,
    stopRecording,
  } = useSpeechRecognition({
    lang: 'en-US',
    continuous: true,
  });
  const value = isRecording ? audioValue : (controlledValue ?? textValue);

  const handleToggleRecording = (newIsRecording: boolean) => {
    if (inputRef.current) {
      if (newIsRecording) {
        startRecording();
        inputRef.current?.focus();
      } else {
        stopRecording();
        const valueLen = inputRef.current.value.length;
        inputRef.current.setSelectionRange(valueLen, valueLen);
        inputRef.current.scrollLeft = inputRef.current.scrollWidth;
      }
    }
  };

  const handleTextChange = (value: string) => {
    if (controlledValue == null) {
      setTextValue(value);
    }
    onValueChange?.(value);

    if (isRecording) {
      stopRecording();
    }
  };

  const handleSearch = useCallback(
    (val = value) => {
      if (val.length > 0) {
        if (onSearch(val) === false) {
          return;
        }
        setTextValue('');
        setAudioValue('');
        onValueChange?.('');
      }
    },
    [onSearch, value, setTextValue, setAudioValue, onValueChange],
  );

  useEffect(() => {
    if (isRecording) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTextValue('');
    } else {
      if (audioValue.length > 0) {
        handleSearch(audioValue);
      }
    }
  }, [isRecording, audioValue, handleSearch]);

  useEffect(() => {
    if (isRecording && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(audioValue.length, audioValue.length);
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }, [isRecording, audioValue, handleSearch, inputRef]);

  return (
    <InputGroup size={size} className={cn(isRound && 'rounded-2xl', className)}>
      {showAIIcon && (
        <InputGroupAddon className="pl-2" align="inline-start">
          <AIStarsIcon />
        </InputGroupAddon>
      )}
      <InputGroupTextInput
        ref={inputRef}
        value={value}
        maxLength={Number.MAX_SAFE_INTEGER}
        size={size}
        placeholder={placeholder}
        onValueChange={handleTextChange}
        onKeyDown={(e) => !isSearchDisabled && e.key === 'Enter' && handleSearch()}
        onFocus={onFocus}
        onBlur={onBlur}
        {...rest}
      />
      <InputGroupAddon className={showSendButton ? 'pr-2' : 'pr-3'} align="inline-end">
        <MicrophoneButton
          isRecording={isRecording}
          size={size === 'large' ? 'large' : 'medium'}
          onRecordingChange={handleToggleRecording}
        />
        {showSendButton && (
          <Button
            data-slot="ai-search-send-button"
            className="h-9.5 w-14"
            variant="primary"
            size="icon"
            shape="square"
            disabled={isSearchDisabled}
            onClick={() => handleSearch()}
          >
            <SendIcon />
          </Button>
        )}
      </InputGroupAddon>
    </InputGroup>
  );
};

export default AISearchInput;
